import { CustomizerEngine } from 'smg/customizer';

import eyedropperCursor from 'smg/img/eyedropper-cursor.svg';

import { BackgroundColorState, BG_RADIUS_NO_RING, BG_RADIUS_WITH_RING, CoinBackgroundColor } from './coin-background-color';

export interface BackgroundColorUIState {
    front: BackgroundColorState;
    back: BackgroundColorState;
}

export class BackgroundColorUI {
    #frontEngine: CustomizerEngine;
    #backEngine: CustomizerEngine;
    #frontBgColor: CoinBackgroundColor;
    #backBgColor: CoinBackgroundColor;
    #currentSide: 'front' | 'back' = 'front';

    // DOM elements
    #toggle: HTMLInputElement | null;
    #controls: HTMLElement | null;
    #swatch: HTMLInputElement | null;
    #hexDisplay: HTMLElement | null;
    #eyedropperBtn: HTMLButtonElement | null;

    // Eyedropper state
    #eyedropperActive: boolean = false;
    #eyedropperCleanup: (() => void) | null = null;

    constructor(frontEngine: CustomizerEngine, backEngine: CustomizerEngine) {
        this.#frontEngine = frontEngine;
        this.#backEngine = backEngine;

        this.#frontBgColor = new CoinBackgroundColor(frontEngine, 'front');
        this.#backBgColor = new CoinBackgroundColor(backEngine, 'back');

        this.#toggle = document.getElementById('bg-color-switch') as HTMLInputElement | null;
        this.#controls = document.getElementById('bg-color-controls');
        this.#swatch = document.getElementById('bg-color-swatch') as HTMLInputElement | null;
        this.#hexDisplay = document.getElementById('bg-color-hex-display');
        this.#eyedropperBtn = document.getElementById('bg-eyedropper-btn') as HTMLButtonElement | null;

        this.#setupToggle();
        this.#setupColorPicker();
        this.#setupEyedropper();
        this.#setupTextRingSync();
        this.#setupUndoRedoSync();
    }

    get #currentEngine(): CustomizerEngine {
        return this.#currentSide === 'front' ? this.#frontEngine : this.#backEngine;
    }

    get #currentBgColor(): CoinBackgroundColor {
        return this.#currentSide === 'front' ? this.#frontBgColor : this.#backBgColor;
    }

    #setupToggle(): void {
        if (!this.#toggle) {
            return;
        }

        const $toggle = this.#toggle;
        $toggle.addEventListener('change', () => {
            const bgColor = this.#currentBgColor;
            const previousState = bgColor.getState();

            if ($toggle.checked) {
                // Turning on - show controls and enable with current swatch color
                this.#showControls();
                const color = this.#swatch?.value ?? '#FFFFFF';
                bgColor.setColor(color);
                bgColor.show();
            } else {
                // Turning off - hide controls and disable
                this.#hideControls();
                bgColor.hide();
            }

            const side = this.#currentSide;
            const newState = bgColor.getState();

            this.#currentEngine.addAction({
                type: $toggle.checked ? 'BACKGROUND_COLOR_ENABLE' : 'BACKGROUND_COLOR_RESET',
                undo: () => {
                    const target = side === 'front' ? this.#frontBgColor : this.#backBgColor;
                    target.setState(previousState);
                },
                redo: () => {
                    const target = side === 'front' ? this.#frontBgColor : this.#backBgColor;
                    target.setState(newState);
                },
            });

            this.#currentEngine.emit('significantChange', { layerId: `${this.#currentSide}-bg-color` });
        });
    }

    #showControls(): void {
        this.#controls?.classList.remove('d-none');
    }

    #hideControls(): void {
        if (this.#eyedropperActive) {
            this.#cancelEyedropper();
        }
        this.#controls?.classList.add('d-none');
    }

    #setupColorPicker(): void {
        if (!this.#swatch) {
            return;
        }

        const $swatch = this.#swatch;
        $swatch.addEventListener('input', () => {
            const color = $swatch.value;
            const bgColor = this.#currentBgColor;

            // Capture previous state BEFORE modifying
            const previousState = bgColor.getState();

            // Auto-enable when user picks a color (toggle is hidden in consolidated UI)
            if (this.#toggle && !this.#toggle.checked) {
                this.#toggle.checked = true;
                this.#showControls();
            }
            bgColor.setColor(color);
            if (!bgColor.isEnabled()) {
                bgColor.show();
            }

            if (this.#hexDisplay) {
                this.#hexDisplay.textContent = color.toUpperCase();
            }

            const newState = bgColor.getState();
            const side = this.#currentSide;

            this.#currentEngine.addAction({
                type: 'BACKGROUND_COLOR_CHANGE',
                undo: () => {
                    const target = side === 'front' ? this.#frontBgColor : this.#backBgColor;
                    target.setState(previousState);
                },
                redo: () => {
                    const target = side === 'front' ? this.#frontBgColor : this.#backBgColor;
                    target.setState(newState);
                },
            });

            this.#currentEngine.emit('significantChange', { layerId: `${this.#currentSide}-bg-color` });
        });
    }

    #setupEyedropper(): void {
        if (!this.#eyedropperBtn) {
            return;
        }

        this.#eyedropperBtn.addEventListener('click', () => {
            if (this.#eyedropperActive) {
                this.#cancelEyedropper();
                return;
            }

            this.#startEyedropper();
        });
    }

    static #eyedropperCursorUrl = `url('data:image/svg+xml;utf8,${encodeURIComponent(eyedropperCursor as string)}') 2 22, crosshair`;

    #startEyedropper(): void {
        this.#eyedropperActive = true;

        this.#eyedropperBtn?.classList.add('active');

        const stage = this.#currentEngine.getStage();

        // Set custom eyedropper cursor on the canvas container
        const canvasContainer = stage.container();
        canvasContainer.style.cursor = BackgroundColorUI.#eyedropperCursorUrl;

        // Intercept clicks at the DOM level (capture phase) so Konva never processes them.
        // This prevents image selection, dragging, and all other Konva interactions.
        const handleClick = (e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            const pos = stage.getPointerPosition();
            if (!pos) {
                this.#cancelEyedropper();
                return;
            }

            // Export the stage to a canvas for pixel sampling
            const exportCanvas = stage.toCanvas();
            const ctx = exportCanvas.getContext('2d');
            if (!ctx) {
                this.#cancelEyedropper();
                return;
            }

            // Account for stage scale: stage coordinates -> canvas pixel coordinates
            const scaleX = exportCanvas.width / stage.width();
            const scaleY = exportCanvas.height / stage.height();
            const pixelX = Math.round(pos.x * scaleX);
            const pixelY = Math.round(pos.y * scaleY);

            const imageData = ctx.getImageData(pixelX, pixelY, 1, 1);
            const [r, g, b] = imageData.data;
            const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();

            // Apply the sampled color via the swatch input (triggers the input event handler)
            if (this.#swatch) {
                this.#swatch.value = hex;
                this.#swatch.dispatchEvent(new Event('input'));
            }

            this.#cancelEyedropper();
        };

        // Also block mousedown/touchstart so Konva can't start drag operations
        const blockEvent = (e: Event) => {
            e.stopPropagation();
            e.preventDefault();
        };

        const handleKeydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.#cancelEyedropper();
            }
        };

        // Use capture phase on the canvas container to intercept before Konva sees the events
        canvasContainer.addEventListener('click', handleClick, true);
        canvasContainer.addEventListener('mousedown', blockEvent, true);
        canvasContainer.addEventListener('touchstart', blockEvent, true);
        document.addEventListener('keydown', handleKeydown);

        this.#eyedropperCleanup = () => {
            canvasContainer.removeEventListener('click', handleClick, true);
            canvasContainer.removeEventListener('mousedown', blockEvent, true);
            canvasContainer.removeEventListener('touchstart', blockEvent, true);
            document.removeEventListener('keydown', handleKeydown);
        };
    }

    #cancelEyedropper(): void {
        this.#eyedropperActive = false;

        this.#eyedropperBtn?.classList.remove('active');

        const canvasContainer = this.#currentEngine.getStage().container();
        canvasContainer.style.cursor = '';

        if (this.#eyedropperCleanup) {
            this.#eyedropperCleanup();
            this.#eyedropperCleanup = null;
        }
    }

    #setupTextRingSync(): void {
        const $textRingToggle = document.getElementById('wants-text-ring-switch') as HTMLInputElement | null;
        if (!$textRingToggle) {
            return;
        }

        const updateRadius = () => {
            const radius = $textRingToggle.checked ? BG_RADIUS_WITH_RING : BG_RADIUS_NO_RING;
            this.#frontBgColor.setRadius(radius);
            this.#backBgColor.setRadius(radius);
        };

        $textRingToggle.addEventListener('change', updateRadius);

        // Set initial radius based on current state
        updateRadius();
    }

    #setupUndoRedoSync(): void {
        const syncUI = () => {
            this.#syncUIFromState();
        };

        this.#frontEngine.on('actionUndone', syncUI);
        this.#frontEngine.on('actionRedone', syncUI);
        this.#backEngine.on('actionUndone', syncUI);
        this.#backEngine.on('actionRedone', syncUI);
    }

    #syncUIFromState(): void {
        const bgColor = this.#currentBgColor;
        const enabled = bgColor.isEnabled();
        const color = bgColor.getColor();

        if (this.#toggle) {
            this.#toggle.checked = enabled;
        }

        if (enabled) {
            this.#showControls();
        } else {
            this.#hideControls();
        }

        if (this.#swatch) {
            this.#swatch.value = enabled ? color : '#FFFFFF';
        }
        if (this.#hexDisplay) {
            this.#hexDisplay.textContent = enabled ? color.toUpperCase() : '#FFFFFF';
        }
    }

    setSide(side: 'front' | 'back'): void {
        // Cancel eyedropper if active when switching sides
        if (this.#eyedropperActive) {
            this.#cancelEyedropper();
        }

        this.#currentSide = side;
        this.#syncUIFromState();
    }

    reset(): void {
        this.#frontBgColor.hide();
        this.#backBgColor.hide();
        this.#frontBgColor.setColor('#FFFFFF');
        this.#backBgColor.setColor('#FFFFFF');
        this.#syncUIFromState();
    }

    getState(): BackgroundColorUIState {
        return {
            front: this.#frontBgColor.getState(),
            back: this.#backBgColor.getState(),
        };
    }

    restoreState(state: BackgroundColorUIState): void {
        this.#frontBgColor.setState(state.front);
        this.#backBgColor.setState(state.back);
        this.#syncUIFromState();
    }
}
